'use es6';

import { Record, List, Set as ImmutableSet } from 'immutable';
import LinkedInDetailedTargetingSkeleton from 'AdsUI/lib/LinkedInDetailedTargetingSkeleton';
import { ENTITY_FINDERS, getLinkedInFacetError, LinkedInTargetingFacets, LinkedInTargetingFacetRecord } from './LinkedInTargetingRecords';
import I18n from 'I18n';
export const DetailedTargetingEntityRecord = class extends Record({
  name: null,
  id: null,
  facet: null,
  group: null,
  audienceSize: null
}) {
  static fromLinkedIn(liEntity, facet) {
    return new DetailedTargetingEntityRecord({
      name: liEntity.name,
      id: liEntity.urn,
      group: I18n.text(`creation.targeting.detailedTargetingTypes.${facet.facetName}`),
      facet: facet.facetName
    });
  }

  static fromFacebook(facebookChild) {
    return new DetailedTargetingEntityRecord({
      name: facebookChild.name,
      id: facebookChild.id,
      facet: facebookChild.type,
      group: !facebookChild.path ? null : facebookChild.name === facebookChild.path[facebookChild.path.length - 1] ? facebookChild.path[facebookChild.path.length - 2] : facebookChild.path[facebookChild.path.length - 1],
      audienceSize: facebookChild.audienceSize
    });
  }

};
export const TargetingFacetNodeTypes = {
  TOP_LEVEL: 'TOP_LEVEL',
  GROUPING: 'GROUPING',
  NETWORK_NODE: 'NETWORK_NODE',
  TARGETING_FACET_CHILD: 'TARGETING_FACET_CHILD',
  LI_YEARS_OF_EXPERIENCE: 'LI_YEARS_OF_EXPERIENCE',
  SEARCH_RESULTS: 'SEARCH_RESULTS'
};

const returnsNull = () => null;

export const TargetingFacetNodeRecord = Record({
  nodeType: null,
  child: null,
  children: null,
  nodeLabel: null,
  nodeId: null,
  networkFacet: null,
  canSearch: false,
  canPrefetch: false,
  loading: false,
  fetched: false,
  getError: returnsNull
});

const parseNode = (node, networkNodeParser) => {
  if (node.networkFacet) {
    const parsedNode = networkNodeParser(node.networkFacet);
    return parsedNode && TargetingFacetNodeRecord(parsedNode);
  }

  const boundNetworkNodeParser = n => parseNode(n, networkNodeParser);

  return TargetingFacetNodeRecord({
    nodeType: TargetingFacetNodeTypes.GROUPING,
    nodeLabel: I18n.text(`creation.targeting.detailedTargetingModal.categories.${node.name}`),
    nodeId: node.name,
    children: List(node.children.map(boundNetworkNodeParser).filter(Boolean)),
    canSearch: false
  });
};

export const buildLinkedInTargetingFacetNodeTree = linkedInTargetingFacets => {
  return TargetingFacetNodeRecord({
    nodeType: TargetingFacetNodeTypes.TOP_LEVEL,
    children: List(LinkedInDetailedTargetingSkeleton).map(node => parseNode(node, facetName => {
      const rawFacet = linkedInTargetingFacets.find(liFacet => liFacet.facetName === facetName);

      if (!rawFacet) {
        return false;
      }

      const facet = LinkedInTargetingFacetRecord(rawFacet);
      return TargetingFacetNodeRecord({
        nodeLabel: I18n.text(`creation.targeting.detailedTargetingTypes.${facet.facetName}`),
        nodeId: facet.facetName,
        nodeType: facet.facetName === LinkedInTargetingFacets.YEARS_OF_EXPERIENCE_RANGES ? TargetingFacetNodeTypes.LI_YEARS_OF_EXPERIENCE : TargetingFacetNodeTypes.NETWORK_NODE,
        networkFacet: facet,
        canSearch: facet.availableEntityFinders.includes(ENTITY_FINDERS.TYPEAHEAD),
        canPrefetch: facet.availableEntityFinders.includes(ENTITY_FINDERS.AD_TARGETING_FACET),
        getError: getLinkedInFacetError.bind(null, facet)
      });
    }))
  });
};
const FACEBOOK_SEARCHABLE_CHILDREN = ['EDUCATION_MAJORS', 'EDUCATION_SCHOOLS', 'WORK_EMPLOYERS', 'WORK_POSITIONS'];

const matchBeginningOfPath = path => child => {
  for (let i = 0; i < path.length; i++) {
    if (path[i] !== child.path[i]) {
      return false;
    }
  }

  return true;
};

const getChildrenForPath = (facebookChildren, path) => {
  const relevantChildren = facebookChildren.filter(matchBeginningOfPath(path));
  const directChildNodes = relevantChildren.filter(child => child.path.length === path.length && (!!child.id || FACEBOOK_SEARCHABLE_CHILDREN.includes(child.type))).map(child => child.id ? TargetingFacetNodeRecord({
    nodeType: TargetingFacetNodeTypes.TARGETING_FACET_CHILD,
    nodeLabel: child.name,
    child: DetailedTargetingEntityRecord.fromFacebook(child)
  }) : TargetingFacetNodeRecord({
    nodeType: TargetingFacetNodeTypes.NETWORK_NODE,
    nodeLabel: child.name,
    nodeId: child.type,
    networkFacet: child,
    canSearch: true,
    canPrefetch: false
  }));
  const indirectChildNodes = ImmutableSet(relevantChildren.filter(child => child.path.length > path.length).map(child => child.path[path.length])).map(newPath => getChildrenForPath(facebookChildren, [...path, newPath]));
  return new TargetingFacetNodeRecord({
    nodeType: TargetingFacetNodeTypes.GROUPING,
    nodeId: path[path.length - 1],
    nodeLabel: path[path.length - 1],
    children: List([...directChildNodes, ...indirectChildNodes]).sortBy(node => node.nodeLabel)
  });
};

export const buildFacebookTargetingNodeTree = facebookChildren => {
  const rootPaths = ImmutableSet(facebookChildren.map(child => child.path[0])).toList();
  return new TargetingFacetNodeRecord({
    nodeType: TargetingFacetNodeTypes.TOP_LEVEL,
    children: rootPaths.map(path => getChildrenForPath(facebookChildren, [path]))
  });
};